"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, TransitionUp } from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import {
  delayTime,
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "ui/transitions/utils"
import { extractProperty, getNonUmbracoUrl, infoMenuClick, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const OuterContainer = styled.div<{ $isGray?: boolean }>`
  width: 100%;
  background-color: ${({ $isGray }) =>
    $isGray ? sharedColors.mediumGrayLight : "transparent"};
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 16px 48px;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 48px 0 64px;
    gap: 48px;
  }
`
const StyledTypography = styled(Typography)<{ $inView: boolean }>`
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
  p {
    margin: 0;
  }
`

const BoxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
  }
`

const BoxGridItem = styled(Link)`
  display: flex;
  width: 100%;
  height: 162px;
  padding: 24px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.borderGray};
  text-decoration: none;
  :hover {
    box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  }
`

type Props = {
  data: any
  blockIndex?: number
  isHomepage?: boolean
}

export default function InformationBlocks({ data, blockIndex, isHomepage }: Props) {
  const description = extractProperty(data, "description")
  const infoIconList = extractProperty(data, "infoIconList") || []
  const infoIconListItems = infoIconList.map((item: any) => ({
    icon: extractProperty(item, "icon"),
    text: extractProperty(item, "text"),
    url: extractProperty(item, "url")?.[0]?.url,
  })) as { icon: string; text: string; url: string }[]
  const stripHtmlTags = (html: string) => {
    return html.replace(/<[^>]*>/g, "")
  }
  const { ref, inView } = useInView(useInViewConfig)
  const isGray = isHomepage && typeof blockIndex === "number" && blockIndex % 2 === 1
  return (
    <OuterContainer $isGray={isGray}>
      <Container>
      <StyledContainer ref={ref}>
        <StyledTypography $inView={inView} variant="body1">
          {stripHtmlTags(description || "")}
        </StyledTypography>
        <BoxGrid>
          {infoIconListItems.map((el, i) => (
            <TransitionUp
              key={i}
              $inView={inView}
              $delay={transitionDelayTime * 0.5 * i}
            >
              <BoxGridItem
                href={getNonUmbracoUrl(el.url) || ""}
                key={i}
                onClick={() => {
                  infoMenuClick(el.text, el.url)
                }}
              >
                <Image
                  src={getUmbracoMediaUrl(el.icon)}
                  alt={el.text}
                  width={56}
                  height={56}
                />
                <Typography variant="body1" style={{ textAlign: "center" }}>
                  {el.text}
                </Typography>
              </BoxGridItem>
            </TransitionUp>
          ))}
        </BoxGrid>
      </StyledContainer>
      </Container>
    </OuterContainer>
  )
}
